import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

import { Toast } from '@ds/feedback';
import type { ToastTone } from '@ds/feedback';

export interface AvisoPedido {
    title: ReactNode;
    description?: ReactNode;
    tone?: ToastTone;
    /** Milissegundos até sumir sozinho. `0` mantém até a pessoa fechar. */
    duracao?: number;
}

interface Aviso extends AvisoPedido {
    id: number;
}

type MostrarAviso = (aviso: AvisoPedido) => void;

const Contexto = createContext<MostrarAviso | null>(null);

const DURACAO_PADRAO = 5000;

/**
 * Avisos do sistema.
 *
 * Substitui o `window.vecToast` do protótipo: uma função global escrita no
 * `window` não dá para testar, não limpa temporizador ao desmontar e obriga
 * toda tela a confiar que alguém a definiu antes.
 */
export function ProvedorDeAvisos({ children }: { children: ReactNode }) {
    const [avisos, setAvisos] = useState<Aviso[]>([]);
    const proximoId = useRef(1);
    const temporizadores = useRef<number[]>([]);

    const fechar = useCallback((id: number) => {
        setAvisos((atuais) => atuais.filter((a) => a.id !== id));
    }, []);

    const mostrar = useCallback<MostrarAviso>((pedido) => {
        const id = proximoId.current;
        proximoId.current += 1;
        setAvisos((atuais) => [...atuais, { ...pedido, id }]);

        const duracao = pedido.duracao ?? DURACAO_PADRAO;
        if (duracao > 0) {
            temporizadores.current.push(window.setTimeout(() => fechar(id), duracao));
        }
    }, [fechar]);

    /* Sem isto, um temporizador pendente chama setState depois da desmontagem —
       o que nos testes vira aviso de vazamento e, na aplicação, trabalho à toa. */
    useEffect(() => () => {
        temporizadores.current.forEach((t) => window.clearTimeout(t));
        temporizadores.current = [];
    }, []);

    const pilha = avisos.length > 0 && typeof document !== 'undefined'
        ? createPortal(
            <div
                style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 90, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
                {avisos.map((a) => (
                    <Toast key={a.id} tone={a.tone} title={a.title} description={a.description} onDismiss={() => fechar(a.id)} />
                ))}
            </div>,
            document.body,
        )
        : null;

    const valor = useMemo(() => mostrar, [mostrar]);

    return (
        <Contexto.Provider value={valor}>
            {children}
            {pilha}
        </Contexto.Provider>
    );
}

/**
 * Devolve a função que mostra um aviso. Fora do provedor ela é inerte, de
 * propósito: um componente isolado num teste não deve quebrar por falta de
 * moldura, e um aviso perdido não é motivo para derrubar a tela.
 */
export function useAviso(): MostrarAviso {
    const mostrar = useContext(Contexto);
    return mostrar ?? (() => { });
}
